import { useEffect, useRef } from 'react';
import { useQuery } from '../../query-context';
import { useKeyboardHighlightProvider } from '@atlassian/search-dialog';
import { useDialogExpansionContext } from '../../dialog-expansion-context';

type useQuickFindResultHighlighterArgs = {
	indexToHighlight: number;
};

/**
 * This hook is responsible for controlling result highlighting for Quick Find.
 * It has the following responsibilities:
 *  - Highlights a specific index when a user enters a query
 *  - Unhighlights the current result when the query is cleared
 *  - Resets the highlighted item when the dialog is closed
 * @param options {@link useQuickFindResultHighlighterArgs}
 */
export const useQuickFindResultHighlighter = ({
	indexToHighlight,
}: useQuickFindResultHighlighterArgs) => {
	const { query } = useQuery();
	const previousQuery = useRef<string>('');

	const { isExpanded } = useDialogExpansionContext();

	const { highlightComponentAt, unhighlightAll, keyboardNavComponents } =
		useKeyboardHighlightProvider();

	useEffect(() => {
		// Do nothing if the highlighting methods do not exist
		if (!highlightComponentAt || !unhighlightAll) {
			return;
		}

		// Do nothing if there is no component to highlight
		if (!keyboardNavComponents || !keyboardNavComponents[indexToHighlight]) {
			return;
		}

		// Reset the highlighting when the dialog is collapsed
		if (!isExpanded) {
			previousQuery.current = '';
			unhighlightAll();
			return;
		}

		// When user starts typing a new query, highlight the first item
		if (previousQuery.current === '' && query !== previousQuery.current) {
			requestAnimationFrame(() => {
				unhighlightAll();
				highlightComponentAt(indexToHighlight);
			});
		}

		// When user clears the query, unhighlight the current
		if (previousQuery.current !== '' && query === '') {
			requestAnimationFrame(() => {
				unhighlightAll();
			});
		}

		// Set the current query as the previous query
		previousQuery.current = query;
	}, [
		highlightComponentAt,
		query,
		unhighlightAll,
		keyboardNavComponents,
		isExpanded,
		indexToHighlight,
	]);

	return null;
};
